import React from 'react'
import styled from 'styled-components'
import tw, { theme } from 'twin.macro'
import { useAmp } from 'next/amp'
import HashTag from './HashTag'
import { SearchArticle } from './ArticleCard'
import { tagUrl } from '@libs/appUrl'
import getConfig from 'next/config'
import PopLink from '@components/PopLink'

interface MobileArticleCardProps {
  article?: SearchArticle
  keyword?: string
  trackEnterEvent?: () => void
  typeFilter?: string
  sortFilter?: string
}

const { publicRuntimeConfig } = getConfig()

export default function MobileArticleCard(props: MobileArticleCardProps) {
  const { article, keyword, trackEnterEvent, typeFilter, sortFilter } = props
  const { articleUrl, publisher, publishedTime, articleImageUrl, hashTags, title, id, tags } =
    article

  const isAmp = useAmp()
  const displayedTags = publicRuntimeConfig.articleTagsEnabled ? tags : []

  return (
    <Container>
      <ArticleImageContainer>
        <PopLink url={articleUrl}>
          <a onClick={trackEnterEvent}>
            {!isAmp ? (
              <ArticleImage>
                <img src={articleImageUrl} />
              </ArticleImage>
            ) : (
              <amp-img src={articleImageUrl} width="100" height="100" layout="responsive" />
            )}
          </a>
        </PopLink>
      </ArticleImageContainer>
      <TextContainer>
        <Tags>
          {displayedTags?.slice(0, 3).map((tag: string, index: number) => {
            return (
              <a key={index} href={tagUrl(tag)}>
                {tag}
              </a>
            )
          })}
          {hashTags
            ?.slice(0, 3 - Math.min(displayedTags?.length || 0, 3))
            .map((hashTag: string) => {
              return (
                <HashTag
                  key={hashTag}
                  tag={hashTag}
                  keyword={keyword}
                  articleId={id}
                  typeFilter={typeFilter}
                  sortFilter={sortFilter}
                />
              )
            })}
        </Tags>
        <PopLink url={articleUrl}>
          <a onClick={trackEnterEvent}>
            <Title>{title}</Title>
          </a>
        </PopLink>
        <Publish href={`/user/${publisher?.mid}`}>
          <span data-datetime={publishedTime.dateTime}>{publishedTime.formatted}</span>
          <span>・</span>
          <span>{publisher?.name}</span>
        </Publish>
      </TextContainer>
    </Container>
  )
}

const Container = styled.div`
  ${tw`flex w-full cursor-pointer transition duration-500`}
  margin-bottom: 20px;
  @media (min-width: ${theme`screens.md`}) {
    ${tw`hidden`}
  }
`

const Publish = styled.a`
  ${tw`mb-2 text-gray-600 text-c2-d`}
`

const ArticleImageContainer = styled.div`
  ${tw`mr-3 flex-shrink-0`}
  width: 80px;
  height: 80px;

  img {
    ${tw`rounded-3 object-cover`}
  }
`

const ArticleImage = styled.div`
  width: 80px;
  height: 80px;

  img {
    ${tw`w-full h-full object-cover`}
  }
`

const TextContainer = styled.div`
  width: 0;
  flex-grow: 1;
`

const Tags = styled.div`
  ${tw`text-primary-indigo mb-1`}
  max-height: 20px;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  a {
    &:not(:last-child)::after {
      ${tw`mx-2`}
      content: '∙';
    }

    ${tw`text-c2`} @media(min-width: 400 px) {
      ${tw`text-h6-d`}
    }
  }
`

const Title = styled.h2`
  ${tw`text-gray-800 text-h5 font-medium`}
  line-height: 135%;
  max-height: 66px;
  /* the workaround for text-overflow: ellipsis; only works in 1 line text */
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`
